import React, { useState } from "react"
import { Card, CardBody, Table, Button, Badge, Spinner } from "reactstrap"
import { Link } from "react-router-dom"
import { isEmpty } from "lodash"
import SAlert from "components/Alert"
import DeleteModal from "components/Common/DeleteModal"
import CustomHeaderTitle from "components/Common/CustomHeaderTitle"
import { useDispatch } from "react-redux"
import {
  deleteProvinceOutDraft,
  deleteManyProvinceOutDrafts,
} from "store/province-out/province-out-action"

import { UncontrolledTooltip } from "reactstrap"

const SubmitedList = ({
  submited = [],
  setSubmited,
  drafts = [],
  loading = false,
  title = "Draft Items",
}) => {
  const dispatch = useDispatch()
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedDraft, setSelectedDraft] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])

  // Group drafts by item_id
  const groupedDrafts = React.useMemo(() => {
    if (!Array.isArray(drafts) || drafts.length === 0) return []

    const groups = drafts.reduce((acc, draft) => {
      if (!draft || !draft.data) return acc

      let draftData
      try {
        draftData =
          typeof draft.data === "string" ? JSON.parse(draft.data) : draft.data
      } catch (e) {
        console.error("Error parsing draft data:", e)
        return acc
      }

      if (!draftData) return acc

      const itemId = draftData.item_id
      if (!acc[itemId]) {
        acc[itemId] = {
          item_id: itemId,
          item_name: draftData.item3 || "Unknown Item",
          drafts: [],
        }
      }
      acc[itemId].drafts.push(draft)
      return acc
    }, {})

    return Object.values(groups)
  }, [drafts])

  // Remove item from temporary submitted list
  const RemoveItemHandler = itemKey => {
    setSubmited(prev => {
      const updateItems = prev.filter((ite, key) => key !== itemKey)
      return [...updateItems]
    })
  }

  // Format date for display
  const formatDate = dateString => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  // Handle single draft deletion
  const handleDeleteDraft = draft => {
    setSelectedDraft(draft)
    setDeleteModal(true)
  }

  // Confirm single draft deletion
  const confirmDeleteDraft = () => {
    if (!selectedDraft) {
      setDeleteModal(false)
      return
    }

    dispatch(deleteProvinceOutDraft(selectedDraft.id))
    setDeleteModal(false)
    setSelectedDraft(null)
  }

  // Handle multiple draft deletion
  const handleDeleteManyDrafts = () => {
    if (selectedIds.length === 0) {
      SAlert({ text: "No drafts selected", status: "info" })
      return
    }
    dispatch(deleteManyProvinceOutDrafts(selectedIds))
    setSelectedIds([])
  }

  // Calculate totals for a group of drafts
  const calculateTotals = groupDrafts => {
    if (isEmpty(groupDrafts) || !Array.isArray(groupDrafts))
      return { totalItems: 0, totalQuantity: 0 }

    return groupDrafts.reduce(
      (acc, draft) => {
        if (!draft || !draft.data) return acc

        let draftData
        try {
          draftData =
            typeof draft.data === "string" ? JSON.parse(draft.data) : draft.data
        } catch (e) {
          console.error("Error parsing draft data:", e, draft.data)
          return acc
        }

        if (!draftData) return acc

        return {
          totalItems: acc.totalItems + 1,
          totalQuantity: acc.totalQuantity + (draftData.out_quantity || 0),
        }
      },
      { totalItems: 0, totalQuantity: 0 }
    )
  }

  // Show temporary submitted items first
  if (submited && submited.length > 0) {
    return (
      <Card className="mt-4">
        <CardBody>
          <CustomHeaderTitle
            loading={false}
            title="Temporary Items"
            lgColumn={4}
            orderingList={false}
            searchBox={false}
            rightBtn={false}
          />
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Province</th>
                  <th>Facility</th>
                  <th>Item</th>
                  <th>Batch Number</th>
                  <th>Quantity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {submited.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.item1}</td>
                    <td>{item.item2 || "N/A"}</td>
                    <td>{item.item3}</td>
                    <td>{item.item4}</td>
                    <td>{item.item5}</td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => RemoveItemHandler(index)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    )
  }

  // Show drafts if no temporary items
  if (loading) {
    return (
      <Card className="mt-4">
        <CardBody className="text-center">
          <Spinner color="primary" />
          <div className="mt-2">Loading drafts...</div>
        </CardBody>
      </Card>
    )
  }

  if (isEmpty(drafts) || !Array.isArray(drafts)) {
    return (
      <Card className="mt-4">
        <CardBody className="text-center text-muted">
          <i className="bx bx-package display-4" />
          <h5 className="mt-2">No Draft Items</h5>
          <p>Start by adding items using the form above.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      {/* Render grouped drafts */}
      {groupedDrafts.map((group, groupIndex) => {
        const totals = calculateTotals(group.drafts)

        return (
          <Card key={group.item_id || groupIndex} className="mt-4">
            <CardBody>
              <CustomHeaderTitle
                loading={false}
                title={group.item_name}
                lgColumn={17}
                orderingList={false}
                searchBox={false}
                rightBtn={false}
                onAllDeleteClicked={handleDeleteManyDrafts}
              />

              {/* Summary Stats */}
              <div className="d-flex gap-3 text-muted mb-3">
                <small>
                  <strong>Total Quantity:</strong>{" "}
                  {totals.totalQuantity.toLocaleString()}
                </small>
              </div>

              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Province</th>
                      <th>Facility</th>
                      <th>Item Description</th>
                      <th>Batch Number</th>
                      <th>Quantity</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.drafts.map((draft, index) => {
                      if (!draft || !draft.data) return null

                      let draftData
                      try {
                        draftData =
                          typeof draft.data === "string"
                            ? JSON.parse(draft.data)
                            : draft.data
                      } catch (e) {
                        console.error(
                          "Error parsing draft data:",
                          e,
                          draft.data
                        )
                        return null
                      }

                      if (!draftData) return null

                      return (
                        <tr key={draft.id || index}>
                          <td>{index + 1}</td>
                          <td>{draftData.item1 || "N/A"}</td>
                          <td>{draftData.item2 || "N/A"}</td>
                          <td>
                            <div>
                              <strong>{draftData.item3 || "N/A"}</strong>
                            </div>
                          </td>
                          <td>
                            <Badge color="light" className="text-dark">
                              {draftData.batch_number || "N/A"}
                            </Badge>
                          </td>
                          <td>
                            <strong>
                              {draftData.out_quantity?.toLocaleString() || "0"}
                            </strong>
                          </td>
                          <td>{formatDate(draftData.date)}</td>
                          <td>
                            <Badge color="warning" className="text-capitalize">
                              Draft
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-3">
                              <Link
                                to="#"
                                className="text-danger"
                                onClick={e => {
                                  e.preventDefault()
                                  handleDeleteDraft(draft)
                                }}
                              >
                                <i
                                  className="mdi mdi-delete font-size-18"
                                  id={`deletetooltip-${draft.id}`}
                                />
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`deletetooltip-${draft.id}`}
                                >
                                  Delete
                                </UncontrolledTooltip>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Footer Summary */}
              <div className="mt-3 p-3 bg-light rounded">
                <div className="row text-center">
                  <div className="col">
                    <h6 className="mb-0">{totals.totalItems}</h6>
                    <small className="text-muted">Total Distributions</small>
                  </div>
                  <div className="col">
                    <h6 className="mb-0">
                      {totals.totalQuantity.toLocaleString()}
                    </h6>
                    <small className="text-muted">Total Quantity</small>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )
      })}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={confirmDeleteDraft}
        onCloseClick={() => setDeleteModal(false)}
      />
    </>
  )
}

export default SubmitedList
